import { getConfig } from './config';
import { emit } from './events';
import { setStoredToken, setStoredUser } from './storage';
import {
  type AuthResult,
  type ConnectAppleMusicOptions,
  type ConnectAppleOptions,
  type ConnectSpotifyOptions,
  FirebirdError
} from './types';

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;
const MESSAGE_TYPE = 'firebird:auth:success';
const STATE_KEY = 'firebird_oauth_state';
const AUTH_CHANNEL = 'firebird:auth';

function generateState(): string {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

function validateUser(
  raw: unknown
): { id: string; email: string; name: string } | null {
  if (
    !raw ||
    typeof raw !== 'object' ||
    typeof (raw as Record<string, unknown>).id !== 'string' ||
    typeof (raw as Record<string, unknown>).email !== 'string' ||
    typeof (raw as Record<string, unknown>).name !== 'string'
  ) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  return {
    id: obj.id as string,
    email: obj.email as string,
    name: obj.name as string
  };
}

/**
 * Exchange credentials for an OAuth start URL via the server.
 * The API key is sent in a header (not a URL) to avoid exposure.
 */
async function getOAuthStartUrl(
  provider: 'spotify' | 'apple',
  config: { baseUrl: string; apiKey: string; campaignSlug: string },
  params: {
    origin: string;
    sdkState: string;
    returnUrl?: string;
    consentSelections?: string[];
  }
): Promise<string> {
  const resp = await fetch(`${config.baseUrl}/api/v1/auth/${provider}/start`, {
    method: 'POST',
    headers: {
      'X-API-Key': config.apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      campaignSlug: config.campaignSlug,
      origin: params.origin,
      sdkState: params.sdkState,
      returnUrl: params.returnUrl,
      consentSelections: params.consentSelections
    })
  });

  if (!resp.ok) {
    let message = `Auth init failed: ${resp.status}`;
    try {
      const body = await resp.json();
      if (body.error) {
        message = body.error;
      }
    } catch {
      // non-JSON response
    }
    throw new FirebirdError('AUTH_INIT_FAILED', message);
  }

  const { url } = await resp.json();
  return url as string;
}

/**
 * Shared popup OAuth flow.
 *
 * The popup is opened synchronously (before any network round-trip) so it
 * stays inside the click's transient activation — otherwise popup blockers,
 * Safari especially, block a `window.open` that runs after an `await`.
 *
 * Completion is signalled two ways, because Apple's login page sends a COOP
 * header that severs `window.opener`:
 *  - `postMessage` from the completion page to the opener (works for Spotify);
 *  - a same-origin `BroadcastChannel` re-broadcast from our returnUrl page
 *    (`handleRedirect`) when the opener was severed.
 * Either delivers the same payload; whichever arrives first wins.
 */
function startPopupOAuth(
  provider: 'spotify' | 'apple',
  popupName: string,
  options?: ConnectSpotifyOptions
): Promise<AuthResult> {
  const config = getConfig();

  return new Promise((resolve, reject) => {
    const origin = window.location.origin;
    const state = generateState();

    // Open the popup synchronously to preserve the user gesture. We don't
    // have the provider URL yet (it needs a server round-trip), so open a
    // blank window and navigate it once the URL is ready.
    const left = Math.round(
      window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
    );
    const top = Math.round(
      window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2
    );
    const popup = window.open(
      'about:blank',
      popupName,
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},popup=yes`
    );

    let settled = false;
    let channel: BroadcastChannel | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      channel?.close();
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };

    const handleResult = (data: unknown) => {
      if (
        settled ||
        !data ||
        typeof data !== 'object' ||
        (data as { type?: unknown }).type !== MESSAGE_TYPE
      ) {
        return;
      }
      const msg = data as {
        token?: unknown;
        user?: unknown;
        sdkState?: unknown;
      };
      if (msg.sdkState !== state) {
        return;
      }
      const user = validateUser(msg.user);
      if (!user || typeof msg.token !== 'string' || !msg.token) {
        return;
      }

      settled = true;
      cleanup();

      const result: AuthResult = { token: msg.token, user };
      setStoredToken(result.token);
      setStoredUser(result.user);

      emit('auth:success', result);
      options?.onSuccess?.(result);
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      const expectedOrigin = new URL(config.baseUrl).origin;
      if (event.origin !== expectedOrigin) {
        return;
      }
      handleResult(event.data);
    };

    // Attach both listeners synchronously, before the popup can navigate
    // back, so neither delivery path can race ahead of us.
    window.addEventListener('message', onMessage);
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.onmessage = (event) => handleResult(event.data);
    } catch {
      channel = null;
    }

    const doAuth = async () => {
      const returnUrl = options?.returnUrl || window.location.href;
      let providerUrl: string;
      try {
        providerUrl = await getOAuthStartUrl(provider, config, {
          origin,
          sdkState: state,
          returnUrl,
          consentSelections: options?.consentSelections
        });
      } catch (err) {
        popup?.close();
        throw err;
      }

      // Persist state for the redirect-mode (mobile / blocked) fallback,
      // where handleRedirect verifies it against the URL.
      try {
        sessionStorage.setItem(STATE_KEY, state);
      } catch {
        // sessionStorage unavailable
      }

      // Popup blocked or closed before we could navigate it — fall back to
      // a full-page redirect.
      if (!popup || popup.closed) {
        cleanup();
        window.location.href = providerUrl;
        return;
      }

      popup.location.href = providerUrl;

      pollTimer = setInterval(() => {
        if (popup.closed && !settled) {
          settled = true;
          cleanup();
          const err = new FirebirdError(
            'AUTH_CANCELLED',
            'Authentication window was closed.'
          );
          emit('auth:error', err);
          options?.onError?.(err);
          reject(err);
        }
      }, 500);
    };

    doAuth().catch((err) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      const error =
        err instanceof FirebirdError
          ? err
          : new FirebirdError('AUTH_INIT_FAILED', String(err));
      emit('auth:error', error);
      options?.onError?.(error);
      reject(error);
    });
  });
}

/**
 * Open a popup to the Spotify OAuth flow on firebirdmusic.app.
 * Falls back to redirect mode if the popup is blocked.
 */
export function connectSpotify(
  options?: ConnectSpotifyOptions
): Promise<AuthResult> {
  return startPopupOAuth('spotify', 'firebird-spotify-auth', options);
}

/**
 * Open a popup to the Sign in with Apple flow on firebirdmusic.app.
 * Falls back to redirect mode if the popup is blocked. Apple returns
 * identity (email/name) only — for Apple Music library access, use
 * connectAppleMusic() instead.
 */
export function connectApple(
  options?: ConnectAppleOptions
): Promise<AuthResult> {
  return startPopupOAuth('apple', 'firebird-apple-auth', options);
}

const MUSICKIT_SCRIPT =
  'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

interface MusicKitInstance {
  authorize: () => Promise<string>;
  unauthorize: () => Promise<void>;
  isAuthorized: boolean;
  musicUserToken: string | null;
}

declare global {
  interface Window {
    MusicKit?: {
      configure: (config: {
        developerToken: string;
        app: { name: string; build: string };
      }) => Promise<MusicKitInstance>;
      getInstance: () => MusicKitInstance;
    };
  }
}

/**
 * Load Apple's MusicKit JS into the host page (idempotent).
 */
async function loadMusicKit(developerToken: string): Promise<MusicKitInstance> {
  await new Promise<void>((resolve) => {
    if (window.MusicKit) {
      resolve();
      return;
    }
    document.addEventListener('musickitloaded', () => resolve(), {
      once: true
    });
    const script = document.createElement('script');
    script.src = MUSICKIT_SCRIPT;
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  });

  if (!window.MusicKit) {
    throw new FirebirdError(
      'MUSICKIT_LOAD_FAILED',
      'MusicKit JS failed to load.'
    );
  }

  // If MusicKit was already configured by another part of the host page,
  // reuse the existing instance instead of reconfiguring (which would
  // reset auth state).
  try {
    const existing = window.MusicKit.getInstance();
    if (existing) {
      return existing;
    }
  } catch {
    // not configured yet — fall through
  }

  return window.MusicKit.configure({
    developerToken,
    app: { name: 'Firebird', build: '1.0.0' }
  });
}

/**
 * Connect Apple Music using MusicKit JS. Loads the MusicKit script on
 * the host page, prompts the user to authorize their Apple Music
 * account, then exchanges the musicUserToken for a Firebird session.
 *
 * Apple Music does NOT return user identity, so the caller must supply
 * an email (typically captured via captureEmail/submitForm first).
 */
export async function connectAppleMusic(
  options: ConnectAppleMusicOptions
): Promise<AuthResult> {
  const config = getConfig();

  try {
    if (!options.email) {
      throw new FirebirdError(
        'EMAIL_REQUIRED',
        'connectAppleMusic requires an email — call captureEmail first or pass one in.'
      );
    }

    // Step 1: get a developer token from Firebird (API-key gated).
    const tokenResp = await fetch(
      `${config.baseUrl}/api/v1/auth/apple-music/token`,
      {
        method: 'POST',
        headers: {
          'X-API-Key': config.apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaignSlug: config.campaignSlug,
          origin: window.location.origin
        })
      }
    );

    if (!tokenResp.ok) {
      let message = `Apple Music init failed: ${tokenResp.status}`;
      try {
        const body = await tokenResp.json();
        if (body.error) {
          message = body.error;
        }
      } catch {
        // non-JSON
      }
      throw new FirebirdError('AUTH_INIT_FAILED', message);
    }

    const { developerToken } = (await tokenResp.json()) as {
      developerToken: string;
    };

    // Step 2: load MusicKit and prompt the user.
    const musicKit = await loadMusicKit(developerToken);
    const musicUserToken = await musicKit.authorize();

    if (!musicUserToken) {
      throw new FirebirdError(
        'AUTH_CANCELLED',
        'User did not authorize Apple Music.'
      );
    }

    // Step 3: complete the flow on the server — creates/updates the user
    // and returns a Firebird JWT.
    const completeResp = await fetch(
      `${config.baseUrl}/api/v1/auth/apple-music/complete`,
      {
        method: 'POST',
        headers: {
          'X-API-Key': config.apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaignSlug: config.campaignSlug,
          origin: window.location.origin,
          musicUserToken,
          email: options.email,
          name: options.name,
          consentSelections: options.consentSelections
        })
      }
    );

    if (!completeResp.ok) {
      let message = `Apple Music sign-in failed: ${completeResp.status}`;
      try {
        const body = await completeResp.json();
        if (body.error) {
          message = body.error;
        }
      } catch {
        // non-JSON
      }
      throw new FirebirdError('AUTH_FAILED', message);
    }

    const { token, user: rawUser } = (await completeResp.json()) as {
      token: string;
      user: unknown;
    };

    const user = validateUser(rawUser);
    if (!user || !token) {
      throw new FirebirdError(
        'AUTH_FAILED',
        'Malformed response from Apple Music sign-in.'
      );
    }

    const result: AuthResult = { token, user };
    setStoredToken(result.token);
    setStoredUser(result.user);

    emit('auth:success', result);
    options.onSuccess?.(result);
    return result;
  } catch (err) {
    const error =
      err instanceof FirebirdError
        ? err
        : new FirebirdError('AUTH_FAILED', String(err));
    emit('auth:error', error);
    options.onError?.(error);
    throw error;
  }
}

/**
 * Handle redirect callback from mobile/popup-blocked flow.
 * Reads token from URL query params set by the completion page redirect.
 * Verifies the state nonce to prevent CSRF / session fixation.
 * Call this on your callback page.
 */
export function handleRedirect(): AuthResult | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('firebird_token');
  const userRaw = params.get('firebird_user');
  const sdkState = params.get('firebird_state');
  // Set by the completion page when it bounces a popup whose opener was
  // severed (Apple COOP) back to this same-origin returnUrl page.
  const isPopupBounce = params.get('firebird_popup') === '1';

  if (!token || !userRaw) {
    return null;
  }

  let user: AuthResult['user'] | null;
  try {
    user = validateUser(JSON.parse(userRaw));
  } catch {
    return null;
  }
  if (!user) {
    return null;
  }

  if (isPopupBounce) {
    // We're the bounced popup, now same-origin as the opener tab. Relay the
    // result over a BroadcastChannel (which survives the severed opener) and
    // close. The opener verifies the state nonce against its in-memory value,
    // so we don't touch storage or emit here.
    try {
      const channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.postMessage({ type: MESSAGE_TYPE, token, user, sdkState });
      // Give the message a moment to flush before tearing down.
      setTimeout(() => {
        channel.close();
        window.close();
      }, 300);
    } catch {
      // BroadcastChannel unavailable — nothing else we can do from here.
    }
    return null;
  }

  // Redirect mode (mobile / popup blocked): verify the state nonce against
  // sessionStorage to prevent CSRF / session fixation.
  try {
    const storedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    if (!sdkState || !storedState || sdkState !== storedState) {
      return null;
    }
  } catch {
    // sessionStorage unavailable — cannot verify state safely
    return null;
  }

  const result: AuthResult = { token, user };

  setStoredToken(token);
  setStoredUser(user);

  emit('auth:success', result);

  // Clean up URL params
  const url = new URL(window.location.href);
  url.searchParams.delete('firebird_token');
  url.searchParams.delete('firebird_user');
  url.searchParams.delete('firebird_state');
  window.history.replaceState({}, '', url.toString());

  return result;
}
